import React from 'react';

import UserFilter from 'components/Filters/UserFilter';

import { Switch } from '@gogame/components';
import { Filter } from '@gogame/filters';
import { dateFNS } from '@gogame/intl-ts';

import {
  FILTER_START_PERIOD,
  FILTER_EXPIRATION_PERIOD,
  FILTER_APPLICATIONS,
  FILTER_DESIGNERS,
  FILTER_CHANNELS,
  FILTER_COMMENTS,
  FILTER_CREATED_BY,
  FILTER_CREATIVE_TYPES,
  FILTER_ENHANCERS,
  FILTER_EXCLUDED_TAGS,
  FILTER_GENRES,
  FILTER_INTERNAL_TAGS,
  FILTER_AI_TAGS,
  FILTER_LANGUAGES,
  FILTER_MEDIA_TYPES,
  FILTER_NETWORKS,
  FILTER_ORIENTATIONS,
  FILTER_FILTER_BY,
  FILTER_OS,
  FILTER_CREATIVES_ADDED,
  FILTER_CONCEPTS_ADDED,
  FILTER_REQUESTED_BY,
  FILTER_RESOLUTIONS,
  FILTER_RATIOS,
  FILTER_SIZES,
  FILTER_STATUSES,
  FILTER_CONCEPT_FAMILIES,
  FILTER_USED_IN_ADS,
  EXPIRATION_PERIOD_PRESETS,
  PERIOD_PRESETS,
  FILTER_LENGTHS,
  FILTER_BUSINESS_PARTNERS,
  FILTER_SHARED_WITH,
  FILTER_IS_NEW,
  FILTER_USED_IN_AFFILIATES,
  FILTER_PROMO_CODES,
  FILTER_OUTSOURCE_STUDIOS,
  FILTER_EXCLUSIVE_FOR_AFFILIATES,
  FILTER_AI_TAGGING_STATUS,
  FILTER_INCLUDE_YT_LINK,
  FILTER_TESTED,
  FILTER_INVERT_AFFILIATE_FILTER,
  FILTER_AI_GENERATED,
  FILTER_PRODUCTION_TYPES,
} from 'scenes/Gallery/GalleryPage/constants';
import {
  loadApplications,
  loadChannels,
  loadComments,
  loadCreatedBy,
  loadCreativeTypes,
  loadEnhancers,
  loadExcludedTags,
  loadGenres,
  loadIncludedTags,
  loadLanguages,
  loadMediaTypes,
  loadNetworks,
  loadOrientations,
  loadFilterBy,
  loadOS,
  loadRequestedBy,
  loadResolutions,
  loadRatios,
  loadSizes,
  loadStatuses,
  loadConceptFamilies,
  loadUsedInAds,
  loadBusinessPartners,
  loadFilterWith,
  loadIsNew,
  loadPromoCodes,
  loadUsedInAffiliates,
  loadAITags,
  loadOutsourceStudio,
  loadDesigners,
  loadExclusiveForAffiliates,
  loadAITaggingStatus,
  loadIncludeYTLink,
  loadTested,
  loadAIGenerated,
  loadProductionTypes,
} from 'scenes/Gallery/GalleryPage/sources/filters';

const DEPENDENCIES = {
  [FILTER_CREATIVE_TYPES]: [FILTER_MEDIA_TYPES],
  [FILTER_MEDIA_TYPES]: [FILTER_CREATIVE_TYPES],
  [FILTER_MEDIA_TYPES]: [FILTER_CREATIVE_TYPES],
  [FILTER_NETWORKS]: [FILTER_APPLICATIONS],
  [FILTER_APPLICATIONS]: [FILTER_NETWORKS, FILTER_GENRES],
  [FILTER_USED_IN_AFFILIATES]: [FILTER_USED_IN_ADS],
};

const CreativesAddedFilter = (props) => {
  return (
    <Filter.DateRangePicker
      timeZone={'utc'}
      title={'Creative Added'}
      placeholder={'Select period'}
      presets={PERIOD_PRESETS}
      name={FILTER_CREATIVES_ADDED}
      maxDate={dateFNS.endOfYear(new Date())}
      {...props}
    />
  );
};
const ConceptsAddedFilter = (props) => {
  return (
    <Filter.DateRangePicker
      timeZone={'utc'}
      title={'Concept Added'}
      placeholder={'Select period'}
      presets={PERIOD_PRESETS}
      name={FILTER_CONCEPTS_ADDED}
      maxDate={dateFNS.endOfYear(new Date())}
      {...props}
    />
  );
};

const StartPeriodFilter = (props) => {
  return (
    <Filter.DateRangePicker
      timeZone={'utc'}
      title={'Start period'}
      placeholder={'Select period'}
      presets={EXPIRATION_PERIOD_PRESETS}
      name={FILTER_START_PERIOD}
      maxDate={dateFNS.endOfYear(new Date())}
      {...props}
    />
  );
};

const ExpirationPeriodFilter = (props) => {
  return (
    <Filter.DateRangePicker
      timeZone={'utc'}
      title={'Expiration period'}
      placeholder={'Select period'}
      presets={EXPIRATION_PERIOD_PRESETS}
      name={FILTER_EXPIRATION_PERIOD}
      maxDate={dateFNS.endOfYear(new Date())}
      {...props}
    />
  );
};

const ApplicationsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Applications'}
      name={FILTER_APPLICATIONS}
      source={loadApplications}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_APPLICATIONS]}
      {...props}
    />
  );
};

const NetworksFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Networks'}
      name={FILTER_NETWORKS}
      source={loadNetworks}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_NETWORKS]}
      {...props}
    />
  );
};

const StatusesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Statuses'}
      name={FILTER_STATUSES}
      source={loadStatuses}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_STATUSES]}
      {...props}
    />
  );
};

const DesignersFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Designers'}
      name={FILTER_DESIGNERS}
      source={loadDesigners}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_DESIGNERS]}
      {...props}
    />
  );
};

const CreativeTypesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Creative Types'}
      name={FILTER_CREATIVE_TYPES}
      source={loadCreativeTypes}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_CREATIVE_TYPES]}
      {...props}
    />
  );
};

const GenresFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Genres'}
      name={FILTER_GENRES}
      source={loadGenres}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_GENRES]}
      {...props}
    />
  );
};

const LanguagesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Languages'}
      name={FILTER_LANGUAGES}
      source={loadLanguages}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_LANGUAGES]}
      {...props}
    />
  );
};

const MediaTypesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Media Types'}
      name={FILTER_MEDIA_TYPES}
      source={loadMediaTypes}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_MEDIA_TYPES]}
      {...props}
    />
  );
};

const OrientationsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Orientations'}
      name={FILTER_ORIENTATIONS}
      source={loadOrientations}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_ORIENTATIONS]}
      {...props}
    />
  );
};

const CreatedByFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Uploaded by'}
      name={FILTER_CREATED_BY}
      source={loadCreatedBy}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_CREATED_BY]}
      {...props}
    />
  );
};

const RequestedByFilter = (props) => {
  return (
    <UserFilter
      title={'Requested by'}
      name={FILTER_REQUESTED_BY}
      source={loadRequestedBy}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_REQUESTED_BY]}
      {...props}
    />
  );
};

const ResolutionsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Resolutions'}
      name={FILTER_RESOLUTIONS}
      source={loadResolutions}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_RESOLUTIONS]}
      {...props}
    />
  );
};

const RatiosFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Ratios'}
      name={FILTER_RATIOS}
      source={loadRatios}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_RATIOS]}
      {...props}
    />
  );
};

const SizesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Sizes'}
      name={FILTER_SIZES}
      source={loadSizes}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_SIZES]}
      {...props}
    />
  );
};

const ExclusiveForAffiliatesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Exclusive for (by creatives)'}
      name={FILTER_EXCLUSIVE_FOR_AFFILIATES}
      source={loadExclusiveForAffiliates}
      {...props}
    />
  );
};

const OSFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'OS'}
      name={FILTER_OS}
      source={loadOS}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_OS]}
      {...props}
    />
  );
};

const ChannelsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Marketing partners'}
      name={FILTER_CHANNELS}
      source={loadChannels}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_SIZES]}
      {...props}
    />
  );
};
const EnhancersFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Enhancers'}
      name={FILTER_ENHANCERS}
      source={loadEnhancers}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_ENHANCERS]}
      {...props}
    />
  );
};
const IncludedTagsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Included Internal Tags'}
      name={FILTER_INTERNAL_TAGS}
      source={loadIncludedTags}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_INTERNAL_TAGS]}
      {...props}
    />
  );
};

const ExcludedTagsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Excluded Internal Tags'}
      name={FILTER_EXCLUDED_TAGS}
      source={loadExcludedTags}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_EXCLUDED_TAGS]}
      {...props}
    />
  );
};
const AITaggingStatusTagsFilter = (props) => {
  return (
    <Filter.Select
      title={'AI Tagging Status'}
      name={FILTER_AI_TAGGING_STATUS}
      source={loadAITaggingStatus}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_AI_TAGGING_STATUS]}
      {...props}
    />
  );
};
const AITagsFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'AI Tags'}
      name={FILTER_AI_TAGS}
      source={loadAITags}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_AI_TAGS]}
      {...props}
    />
  );
};

const CommentsFilter = (props) => {
  return (
    <Filter.Select
      title={'Comments'}
      name={FILTER_COMMENTS}
      source={loadComments}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_COMMENTS]}
      {...props}
    />
  );
};
const FilterByFilter = (props) => {
  return (
    <Filter.Select
      title={'Filter by'}
      name={FILTER_FILTER_BY}
      source={loadFilterBy}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_FILTER_BY]}
      {...props}
    />
  );
};
const ConceptFamiliesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Concept Families'}
      name={FILTER_CONCEPT_FAMILIES}
      source={loadConceptFamilies}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_CONCEPT_FAMILIES]}
      {...props}
    />
  );
};

const UsedInAdsFilter = (props) => {
  return (
    <Filter.Select
      title={'Used in Ads (ALL)'}
      name={FILTER_USED_IN_ADS}
      source={loadUsedInAds}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_USED_IN_ADS]}
      {...props}
    />
  );
};

const InvertAffiliateFilter = ({ disabled }) => {
  return (
    <Filter
      name={FILTER_INVERT_AFFILIATE_FILTER}
      renderComponent={({ value, onChange }) => (
        <Switch
          label={'Used in'}
          data-cy-label={'Used in'}
          checked={!value}
          onChange={(e) => onChange(e, !value)}
          disabled={disabled}
          size={'small'}
          sx={{ margin: 0 }}
        />
      )}
    />
  );
};

const UsedInAffiliatesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Used in Ads (Affiliates)'}
      name={FILTER_USED_IN_AFFILIATES}
      source={loadUsedInAffiliates}
      withAll={false}
      placeholder={'All'}
      disableEmpty={true}
      lazy={false}
      depends={DEPENDENCIES[FILTER_USED_IN_AFFILIATES]}
      suffix={({ disabled }) => <InvertAffiliateFilter disabled={disabled} />}
      {...props}
    />
  );
};

const LengthsFilter = (props) => {
  return (
    <Filter.NumberRangeInput
      title={'Lengths (sec)'}
      name={FILTER_LENGTHS}
      {...props}
    />
  );
};

const BusinessPartnersFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Uploaded by Business Partner'}
      name={FILTER_BUSINESS_PARTNERS}
      source={loadBusinessPartners}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_BUSINESS_PARTNERS]}
      {...props}
    />
  );
};

const SharedWithFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Shared with'}
      name={FILTER_SHARED_WITH}
      source={loadFilterWith}
      withAll={false}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_SHARED_WITH]}
      {...props}
    />
  );
};
const IsNewFilter = (props) => {
  return (
    <Filter.Select
      title={'New'}
      name={FILTER_IS_NEW}
      source={loadIsNew}
      placeholder={'All'}
      depends={DEPENDENCIES[FILTER_IS_NEW]}
      {...props}
    />
  );
};

const PromoCodesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Promo Codes'}
      name={FILTER_PROMO_CODES}
      source={loadPromoCodes}
      withAll={false}
      placeholder={'All'}
      {...props}
    />
  );
};

const OutsourceStudiosFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Outsource Studios'}
      name={FILTER_OUTSOURCE_STUDIOS}
      source={loadOutsourceStudio}
      withAll={false}
      placeholder={'All'}
      {...props}
    />
  );
};

const IncludeYTLinkFilter = (props) => {
  return (
    <Filter.Select
      title={'Include YT link'}
      name={FILTER_INCLUDE_YT_LINK}
      source={loadIncludeYTLink}
      placeholder={'All'}
      {...props}
    />
  );
};

const TestedFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Tested'}
      name={FILTER_TESTED}
      source={loadTested}
      {...props}
    />
  );
};

const AIGeneratedFilter = (props) => {
  return (
    <Filter.Select
      title={'AI Generated'}
      name={FILTER_AI_GENERATED}
      source={loadAIGenerated}
      placeholder={'All'}
      {...props}
    />
  );
};

const ProductionTypesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Production types'}
      name={FILTER_PRODUCTION_TYPES}
      source={loadProductionTypes}
      placeholder={'All'}
      withAll={false}
      {...props}
    />
  );
};

export default {
  CreativesAdded: CreativesAddedFilter,
  ConceptsAdded: ConceptsAddedFilter,
  StartPeriod: StartPeriodFilter,
  ExpirationPeriod: ExpirationPeriodFilter,
  Applications: ApplicationsFilter,
  Networks: NetworksFilter,
  Statuses: StatusesFilter,
  Designers: DesignersFilter,
  CreativeTypes: CreativeTypesFilter,
  Genres: GenresFilter,
  Languages: LanguagesFilter,
  MediaTypes: MediaTypesFilter,
  Orientations: OrientationsFilter,
  CreatedBy: CreatedByFilter,
  RequestedBy: RequestedByFilter,
  Resolutions: ResolutionsFilter,
  Ratios: RatiosFilter,
  Sizes: SizesFilter,
  OS: OSFilter,
  ExclusiveForAffiliates: ExclusiveForAffiliatesFilter,
  Channels: ChannelsFilter,
  Enhancers: EnhancersFilter,
  IncludedTags: IncludedTagsFilter,
  ExcludedTags: ExcludedTagsFilter,
  AITaggingStatusTags: AITaggingStatusTagsFilter,
  AITags: AITagsFilter,
  Comments: CommentsFilter,
  FilterBy: FilterByFilter,
  ConceptFamilies: ConceptFamiliesFilter,
  UsedInAds: UsedInAdsFilter,
  UsedInAffiliates: UsedInAffiliatesFilter,
  Lengths: LengthsFilter,
  BusinessPartners: BusinessPartnersFilter,
  SharedWith: SharedWithFilter,
  IsNew: IsNewFilter,
  PromoCodes: PromoCodesFilter,
  OutsourceStudios: OutsourceStudiosFilter,
  IncludeYTLink: IncludeYTLinkFilter,
  Tested: TestedFilter,
  AIGenerated: AIGeneratedFilter,
  ProductionTypes: ProductionTypesFilter,
};
